import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, generatePath } from 'react-router-dom';
import {
  ButtonPseudo,
  ButtonPrimary,
  SidePanel,
  SidePanelType,
  SidePanelLoader,
  ResponseError,
  usePrevious,
  responseErrorsResolver,
  PermissionEnum,
  Popup,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearFeeFormState } from './store/slices/fee-form';
import FeeForm from '../../components/tariffs/fee-form';
import { useLazyGetFeesListQuery } from '../fees/store/services';
import {
  useCreateTariffFeeMutation,
  useUpdateTariffFeeMutation,
} from './store/services';
import { TariffItem, FeeFormValues, FeeAmountTypeEnum } from './types';

import { TariffsRoutes } from '../../routes';

const FeeFormHOC = () => {
  const {
    tariffs: {
      tariffPage: { generalInfo },
      feeForm: { isLoading, feeTypes },
    },
    configurationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [manualFee, setManualFee] = useState(false);
  const [regularFee, setRegularFee] = useState(false);
  const [feeIsLoading, setFeeIsLoading] = useState(true);
  const [defaultFeeValue, setDefaultFeeValue] = useState<FeeFormValues>();
  const [globalError, setGlobalError] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setError,
    setValue,
    watch,
    reset,
    clearErrors,
  } = useForm<FeeFormValues>({
    defaultValues: {
      feeId: '',
      name: '',
      feeTargetAccountNumber: '',
    },
  });
  const [panelIsOpen, setPanelIsOpen] = useState(false);

  const feeType = watch('feeId');
  const feeName = watch('name');
  const writeOffType = watch('feeAmountType');
  const feeCurrencyId = watch('feeCurrencyId');
  const feeCascadeCurrenciesIds = watch('feeCascadeCurrenciesIds');
  const prevWriteOffType = usePrevious(writeOffType);
  const prevFeeCurrencyId = usePrevious(feeCurrencyId);

  const { tariffId, feeId } = useParams();

  const [getFeesList] = useLazyGetFeesListQuery();
  const [createFee, { isLoading: requestInProgress, isSuccess }] =
    useCreateTariffFeeMutation();
  const [updateFee, { isLoading: updateInProgress }] =
    useUpdateTariffFeeMutation();

  const getFeesListAction = useCallback(() => {
    getFeesList();
  }, []);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    reset({});
    setFeeIsLoading(true);

    setTimeout(() => {
      navigate(
        generatePath(TariffsRoutes.TARIFFS_INFO, {
          tariffId: tariffId as string,
        }),
      );
    }, 100);
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    if (!values.feeAmountType) {
      setError('feeAmountType', {
        type: 'custom',
        message: 'Write-off type is required',
      });
    } else if (
      values.feeAmountType &&
      values.feeAmountType === 'FIXED' &&
      !values.feeFixedAmount
    ) {
      setError('feeFixedAmount', {
        type: 'custom',
        message: 'Write-off value is required',
      });
    } else if (
      values.feeAmountType === 'PERCENT' &&
      (!values.feePercentAmount || !values.feePercentMinAmount)
    ) {
      if (!values.feePercentAmount) {
        setError('feePercentAmount', {
          type: 'custom',
          message: 'Write-off value is required',
        });
      }

      if (!values.feePercentMinAmount) {
        setError('feePercentMinAmount', {
          type: 'custom',
          message: 'Minimum amount allowed',
        });
      }
    } else {
      const body: {
        feeId?: string;
        name: string;
        feeTargetAccountNumber?: string;
        feePercentAmount?: number;
        feeFixedAmount?: number;
        feePercentMinAmount?: number | null;
        feePercentMaxAmount?: number | null;
        feeAmountType: string;
        feeCurrencyId?: string;
        feeCascadeCurrenciesIds?: string[];
      } = {
        name: values.name,
        feeAmountType: values.feeAmountType,
      };
      if (values.feeTargetAccountNumber) {
        body.feeTargetAccountNumber = values.feeTargetAccountNumber;
      }
      if (values.feePercentAmount) {
        body.feePercentAmount = Number(values.feePercentAmount);
      }

      if (values.feePercentMinAmount) {
        body.feePercentMinAmount = Number(values.feePercentMinAmount);
      }

      if (values.feePercentMaxAmount) {
        body.feePercentMaxAmount = Number(values.feePercentMaxAmount);
      }

      if (values.feeFixedAmount) {
        body.feeFixedAmount = Number(values.feeFixedAmount);
      }

      if (
        values.feeAmountType !== 'PERCENT' &&
        defaultFeeValue?.feeAmountType === 'PERCENT'
      ) {
        body.feePercentMaxAmount = null;
        body.feePercentMinAmount = null;
      }

      if (values.feeCurrencyId) {
        body.feeCurrencyId = values.feeCurrencyId;
      }

      if (values.feeCascadeCurrenciesIds) {
        body.feeCascadeCurrenciesIds = values.feeCascadeCurrenciesIds;
      }

      let response;
      if (feeId) {
        response = (await updateFee({
          id: tariffId as string,
          feeId,
          body,
        })) as {
          data?: { data: TariffItem };
          error: ResponseError;
        };
      } else {
        const selectedFee = generalInfo?.fees.find(
          (fee) => fee.feeId === values.feeId,
        );

        if (selectedFee) {
          setError('feeId', {
            type: 'custom',
            message: 'This fee type is already in the tariff',
          });
        } else {
          body.feeId = values.feeId;
          response = (await createFee({
            id: tariffId as string,
            body,
          })) as {
            data?: { data: TariffItem };
            error: ResponseError;
          };
        }
      }

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      }
    }
  });

  useEffect(() => {
    if (
      prevWriteOffType &&
      prevWriteOffType !== writeOffType &&
      prevWriteOffType === FeeAmountTypeEnum.PERCENT
    ) {
      reset({
        feeId: feeType,
        name: feeName,
        feeAmountType: writeOffType,
      });
    }
  }, [writeOffType, prevWriteOffType, reset]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (feeId) {
      const currentFee = generalInfo?.fees.find((item) => item.id === feeId);

      if (currentFee) {
        const body: FeeFormValues = {
          feeId: currentFee.feeId,
          name: currentFee.name,
          feeAmountType: currentFee.feeAmountType,
          feeTargetAccountNumber: currentFee?.feeTargetAccountNumber,
        };

        if (currentFee?.fee?.feeType === 'REGULAR') {
          body.feeCurrencyId = currentFee.feeCurrencyId;
          body.feeCascadeCurrenciesIds = currentFee?.feeCascadeCurrenciesIds;

          setRegularFee(true);
        }

        if (currentFee.feeAmountType === FeeAmountTypeEnum.FIXED) {
          body.feeFixedAmount = currentFee.feeFixedAmount;
        } else if (currentFee.feeAmountType === FeeAmountTypeEnum.PERCENT) {
          body.feePercentAmount = currentFee?.feePercentAmount;
          body.feePercentMinAmount = currentFee?.feePercentMinAmount;
          body.feePercentMaxAmount = currentFee?.feePercentMaxAmount;
        }

        setDefaultFeeValue(body);
        reset(body);

        setTimeout(() => {
          setFeeIsLoading(false);
        }, 700);
      }
    }

    return () => {
      dispatch(clearFeeFormState());
    };
  }, [dispatch, feeId, getFeesListAction, feeTypes, generalInfo, reset]);

  useEffect(() => {
    if (
      feeCurrencyId &&
      prevFeeCurrencyId &&
      feeCurrencyId !== prevFeeCurrencyId
    ) {
      setValue('feeFixedAmount', 0);
    }
  }, [feeCurrencyId, prevFeeCurrencyId]);

  useEffect(() => {
    getFeesListAction();
  }, []);

  useEffect(() => {
    if (feeType) {
      feeTypes?.forEach((item) => {
        const currentFee = item.list.find((fee) => fee.value === feeType);

        if (currentFee) {
          if (item.type === 'MANUAL' && !manualFee) {
            setManualFee(true);
          } else if (item.type !== 'MANUAL' && manualFee) {
            setManualFee(false);
          }

          if (item.type === 'REGULAR' && !regularFee) {
            setRegularFee(true);
          } else if (item.type !== 'REGULAR' && regularFee) {
            setRegularFee(false);
          }
        }
      });
    }
  }, [feeType, manualFee, regularFee]);

  useEffect(() => {
    if (!feeId) {
      setFeeIsLoading(false);
    }
  }, []);

  let buttonText;
  let content;
  let title;

  if (feeId) {
    buttonText = 'Save';
  } else {
    buttonText = 'Add Fee';
  }

  if (feeId) {
    title = 'Edit fee';

    if (!activePermissions?.includes(PermissionEnum.FEES_EDIT_ACTION)) {
      title = 'Fee info';
    }
  } else {
    title = 'Add fee to tariff';
  }

  if (isLoading || feeIsLoading) {
    content = <SidePanelLoader />;
  } else if (
    !feeId &&
    !activePermissions?.includes(PermissionEnum.FEES_CREATE_ACTION)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <FeeForm
        feeTypes={feeTypes?.filter((item) => item.list.length)}
        control={control}
        errors={errors}
        feeType={feeType}
        feeId={feeId}
        setValue={setValue}
        writeOffType={writeOffType}
        isEditable={activePermissions?.includes(
          PermissionEnum.FEES_EDIT_ACTION,
        )}
        manualFee={manualFee}
        regularFee={regularFee}
        feeCurrencyId={feeCurrencyId}
        feeCascadeCurrenciesIds={feeCascadeCurrenciesIds}
        reset={reset}
        clearErrors={clearErrors}
        // auditLogAvailable={activePermissions?.includes(
        //   PermissionEnum.AUDIT_LOG_LIST,
        // )}
      />
    );
  }

  const buttonVisible = feeId
    ? activePermissions?.includes(PermissionEnum.FEES_EDIT_ACTION)
    : activePermissions?.includes(PermissionEnum.FEES_CREATE_ACTION);

  return (
    <>
      <SidePanel
        type={SidePanelType.Fees}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={globalError}
        sidepanelExpandedKey="tariff-fee"
        footer={
          <>
            {buttonVisible && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : buttonText
                }
                disabled={Boolean(!feeName && !feeType) || isLoading}
                onClick={onSubmit}
                bold
              />
            )}

            <ButtonPseudo
              text="Close"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default FeeFormHOC;
